// src/utils/pipelineHelpers.js

export function parseJsonLike(v, fallback = {}) {
    if (!v) return fallback;
    if (typeof v === "object") return v;
    if (typeof v === "string") {
        try {
            return JSON.parse(v);
        } catch {
            return fallback;
        }
    }
    return fallback;
}

export function statusBadgeClass(status) {
    const s = String(status || "").toUpperCase();
    if (s === "AVAILABLE" || s === "FINISHED") return "bg-soft-success text-success";
    if (s === "UPLOADING") return "bg-soft-warning text-warning";
    if (s === "FAILED" || s === "ERROR") return "bg-soft-danger text-danger";
    return "bg-light text-muted border";
}

export function filterByKeyword(list, keyword) {
    const kw = String(keyword || "").trim().toLowerCase();
    if (!kw) return list || [];
    return (list || []).filter((x) => {
        const n = (x.display_name || "").toLowerCase();
        const a = (x.ancestry || "").toLowerCase();
        const d = (x.description || "").toLowerCase();
        return n.includes(kw) || a.includes(kw) || d.includes(kw);
    });
}

export function maxPage(total, pageSize) {
    return Math.max(1, Math.ceil((Number(total) || 0) / (Number(pageSize) || 1)));
}

/**
 * 通用加载文件列表：只做一件事——请求+关键字过滤+写回 state
 * 你传入 getter/setter（避免强绑 this）
 */
export async function loadFileList({
    listFiles,            // api function
    category,             // "gwas" / "LD" ...
    page,
    pageSize,
    keyword,
    setLoading,           // (bool)=>void
    setError,             // (msg)=>void
    setList,              // (arr)=>void
    setTotal,             // (n)=>void
}) {
    try {
        setLoading(true);
        setError("");

        const res = await listFiles({
            category,
            page_size: pageSize,
            page_number: page,
        });

        if (res.code !== 0) {
            setList([]);
            setTotal(0);
            setError(res.message || "Failed to load files");
            return;
        }

        const raw = res.data?.file_list || [];
        const total = res.data?.total_count || 0;

        setTotal(total);
        setList(filterByKeyword(raw, keyword));
    } catch (e) {
        console.error("[loadFileList] error:", e);
        setError(e?.message || "Fetch error");
        setList([]);
        setTotal(0);
    } finally {
        setLoading(false);
    }
}

/**
 * Step1 映射注入（GWAS -> summary_statistic_2, LD -> ldref_0）
 * algo: { parameters: [...] }
 */
export function applyStep1MappingToAlgo({ algo, gwasKey, ldKey }) {
    if (!algo || !Array.isArray(algo.parameters)) return;

    algo.parameters.forEach((p) => {
        const k = String(p.wdlKey || "").toLowerCase();

        if (k.endsWith("summary_statistic_2")) {
            if (!p.isArray && (p.setValue === "" || p.setValue === null || p.setValue === undefined)) {
                p.setValue = gwasKey || "";
            }
        }

        if (k.endsWith("ldref_0")) {
            if (!p.isArray && (p.setValue === "" || p.setValue === null || p.setValue === undefined)) {
                p.setValue = ldKey || "";
            }
        }
    });
}

export function normalizeValueForCromwell(p, v) {
    if (v === "" || v === null || v === undefined) return v;

    if (p.isArray) {
        const arr = Array.isArray(v) ? v : [v];
        return arr.map((x) => {
            if (typeof x === "number") return x;
            const nx = Number(x);
            return Number.isFinite(nx) ? nx : x;
        });
    }

    if (p.schemaType === "integer") {
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : v;
    }
    if (p.schemaType === "number") {
        const n = Number(v);
        return Number.isFinite(n) ? n : v;
    }
    if (p.schemaType === "boolean") {
        if (typeof v === "boolean") return v;
        if (v === "true" || v === "1" || v === 1) return true;
        if (v === "false" || v === "0" || v === 0) return false;
        return !!v;
    }
    return v;
}

export function prettyJson(obj) {
    try {
        return JSON.stringify(obj, null, 2);
    } catch {
        return "{}";
    }
}

export function stringifyDefault(v) {
    if (Array.isArray(v)) return JSON.stringify(v);
    if (v === null || v === undefined) return "";
    return String(v);
}